import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, LogIn, LogOut, Menu, X } from "lucide-react";
import { SHOP_URL, NAV_LINKS } from "../data/content";
import { useAuth } from "../context/AuthContext";

export function BrandGlyph({ size = 26 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <circle
        cx="16"
        cy="16"
        r="12"
        stroke="rgba(167,139,250,0.7)"
        strokeWidth="1"
        strokeDasharray="2 5"
        className="animate-spin-slow"
        style={{ transformOrigin: "16px 16px" }}
      />
      <circle cx="16" cy="16" r="6.5" stroke="rgba(56,189,248,0.8)" strokeWidth="1" />
      <circle cx="16" cy="16" r="1.8" fill="#F0F2F8" />
    </svg>
  );
}

export default function NavigationHeader() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { user, login, logout } = useAuth();

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    if (window.__lenis) open ? window.__lenis.stop() : window.__lenis.start();
    return () => {
      document.body.style.overflow = "";
      if (window.__lenis) window.__lenis.start();
    };
  }, [open]);

  const linkClass = ({ isActive }) =>
    `relative py-2 text-[13px] uppercase tracking-[0.08em] transition-colors duration-300 ${
      isActive ? "text-[#F0F2F8]" : "text-[#8E95A5] hover:text-[#F0F2F8]"
    }`;

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 border-b transition-all duration-500 ${
          scrolled || open
            ? "glass-panel border-white/[0.08]"
            : "border-transparent bg-transparent"
        }`}
      >
        <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 sm:px-8 lg:px-10">
          <Link
            to="/"
            data-testid="nav-brand-link"
            className="group flex items-center gap-3"
          >
            <BrandGlyph />
            <span className="leading-tight">
              <span className="block font-display text-lg tracking-wide text-[#F0F2F8] transition-colors duration-300 group-hover:text-[#A78BFA]">
                Consciousness Ascending
              </span>
              <span className="block font-mono-ui text-[9px] uppercase tracking-[0.34em] text-[#5F6677]">
                Your Own Remote Path
              </span>
            </span>
          </Link>

          <nav className="hidden items-center gap-7 lg:flex" aria-label="Primary">
            {NAV_LINKS.slice(0, 6).map((link) => (
              <NavLink
                key={link.id}
                to={link.path}
                data-testid={`nav-link-${link.id}`}
                className={linkClass}
              >
                {({ isActive }) => (
                  <>
                    {link.name}
                    {isActive && (
                      <span className="absolute -bottom-0.5 left-0 h-px w-full bg-gradient-to-r from-[#8B5CF6] to-[#38BDF8]" />
                    )}
                  </>
                )}
              </NavLink>
            ))}
            {user ? (
              <Link
                to="/account"
                data-testid="nav-user-chip"
                className="flex items-center gap-2.5 rounded-full border border-[#8B5CF6]/40 py-1 pl-1 pr-4 transition-all duration-300 hover:border-[#8B5CF6] hover:shadow-[0_0_20px_rgba(139,92,246,0.3)]"
              >
                {user.picture ? (
                  <img
                    src={user.picture}
                    alt=""
                    className="h-7 w-7 rounded-full border border-[#8B5CF6]/50 object-cover"
                  />
                ) : (
                  <span className="flex h-7 w-7 items-center justify-center rounded-full border border-[#8B5CF6]/50 bg-[#8B5CF6]/15 font-display text-sm text-[#A78BFA]">
                    {(user.name || user.email || "?").charAt(0).toUpperCase()}
                  </span>
                )}
                <span className="text-[12px] text-[#F0F2F8]">
                  {user.name?.split(" ")[0] || "Account"}
                </span>
              </Link>
            ) : (
              <button
                data-testid="nav-signin-btn"
                onClick={login}
                className="inline-flex items-center gap-2 border border-[#38BDF8]/40 px-4 py-2 font-mono-ui text-[11px] uppercase tracking-[0.18em] text-[#F0F2F8] transition-all duration-300 hover:border-[#38BDF8] hover:bg-[#38BDF8]/10 hover:shadow-[0_0_20px_rgba(56,189,248,0.3)]"
              >
                <LogIn className="h-3.5 w-3.5 text-[#7DD3FC]" /> Sign in
              </button>
            )}
            <a
              data-testid="nav-fourthwall-cta"
              href={SHOP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full border border-[#8B5CF6]/40 px-4 py-2 font-mono-ui text-[11px] uppercase tracking-[0.18em] text-[#F0F2F8] transition-all duration-300 hover:border-[#8B5CF6] hover:bg-[#8B5CF6]/15 hover:shadow-[0_0_20px_rgba(139,92,246,0.3)]"
            >
              Shop <ArrowUpRight className="h-3.5 w-3.5 text-[#A78BFA]" />
            </a>
          </nav>

          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(!open)}
            aria-expanded={open}
            aria-label={open ? "Close menu" : "Open menu"}
            className="flex h-10 w-10 items-center justify-center border border-white/10 text-[#F0F2F8] lg:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="glass-panel fixed inset-0 z-40 flex flex-col justify-between px-6 pb-10 pt-28 lg:hidden"
            data-testid="nav-mobile-drawer"
          >
            <nav className="flex flex-col gap-1" aria-label="Mobile">
              {NAV_LINKS.map((link, i) => (
                <motion.div
                  key={link.id}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i, duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                >
                  <NavLink
                    to={link.path}
                    data-testid={`nav-mobile-link-${link.id}`}
                    className={({ isActive }) =>
                      `flex items-baseline gap-4 border-b border-white/[0.06] py-4 font-display text-3xl ${
                        isActive ? "text-[#A78BFA]" : "text-[#F0F2F8]"
                      }`
                    }
                  >
                    <span className="font-mono-ui text-[11px] tracking-[0.2em] text-[#5F6677]">
                      0{i + 1}
                    </span>
                    {link.name}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <div className="flex flex-col gap-3">
              {user ? (
                <>
                  <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="flex items-center justify-between border border-white/[0.08] px-5 py-4"
                  >
                    <Link
                      to="/account"
                      data-testid="nav-mobile-account-link"
                      className="flex items-center gap-3"
                    >
                      {user.picture ? (
                        <img
                          src={user.picture}
                          alt=""
                          className="h-8 w-8 rounded-full border border-[#8B5CF6]/50 object-cover"
                        />
                      ) : (
                        <span className="flex h-8 w-8 items-center justify-center rounded-full border border-[#8B5CF6]/50 bg-[#8B5CF6]/15 font-display text-sm text-[#A78BFA]">
                          {(user.name || user.email || "?").charAt(0).toUpperCase()}
                        </span>
                      )}
                      <span className="text-sm text-[#F0F2F8]">
                        {user.name || "Your account"}
                      </span>
                    </Link>
                    <button
                      data-testid="nav-mobile-signout-btn"
                      onClick={logout}
                      aria-label="Sign out"
                      className="flex h-9 w-9 items-center justify-center border border-white/15 text-[#8E95A5] transition-colors duration-300 hover:border-red-400/60 hover:text-red-300"
                    >
                      <LogOut className="h-4 w-4" />
                    </button>
                  </motion.div>
                </>
              ) : (
                <motion.button
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  data-testid="nav-mobile-signin-btn"
                  onClick={login}
                  className="inline-flex items-center justify-center gap-2 border border-[#38BDF8]/50 bg-[#38BDF8]/10 px-6 py-4 font-mono-ui text-xs uppercase tracking-[0.2em] text-[#F0F2F8]"
                >
                  <LogIn className="h-4 w-4 text-[#7DD3FC]" /> Sign in with Google
                </motion.button>
              )}
              <motion.a
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.45 }}
                data-testid="nav-mobile-fourthwall-cta"
                href={SHOP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 border border-[#8B5CF6]/50 bg-[#8B5CF6]/10 px-6 py-4 font-mono-ui text-xs uppercase tracking-[0.2em] text-[#F0F2F8]"
              >
                Visit the Fourthwall Shop
                <ArrowUpRight className="h-4 w-4 text-[#A78BFA]" />
              </motion.a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
